import type { ConversationType } from '@components/chat/types'
import { isSystemMessage } from '@components/chat/types'
import { isHasAccess, SystemRoles } from '@features/auth/utils/systemRoles'
import { useAuthenticator } from '@features/auth/utils/useAuthenticator'
import { useGetConversations } from '@features/conversation/hooks/useGetConversations'
import Create from '@mui/icons-material/Create'
import DocumentScanner from '@mui/icons-material/DocumentScanner'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Typography from '@mui/material/Typography/Typography'
import { Link, useLocation, useParams } from '@tanstack/react-router'
import dayjs from 'dayjs'
import i18n from 'dayjs/plugin/localizedFormat'
import { useTranslation } from 'react-i18next'

dayjs.extend(i18n)

const groupByMonth = (items: ConversationType[]): { name: string; conversations: ConversationType[] }[] => {
  const grouped = items.reduce(
    (acc, item) => {
      const date = dayjs(item.created_at)
      const monthKey = date.format('MMMM YYYY')

      if (!acc[monthKey]) {
        acc[monthKey] = []
      }
      acc[monthKey].push(item)

      return acc
    },
    {} as Record<string, ConversationType[]>
  )

  return Object.keys(grouped || {}).map((month) => ({
    name: month,
    conversations: grouped?.[month] || [],
  }))
}

export const Menu = () => {
  const { data, fetchNextPage, hasNextPage, isError } = useGetConversations()
  const { id } = useParams({ strict: false })
  const location = useLocation()
  const { t } = useTranslation()
  const { user } = useAuthenticator()

  const basicMenu: { name: string; Icon: typeof Create; href: string; role?: SystemRoles }[] = [
    { name: t('new_chat.title'), Icon: Create, href: '/new' },
    { name: t('documents.title'), Icon: DocumentScanner, href: '/indexed-files' },
  ]

  const conversationHistory: Array<ConversationType> = data ?? []

  return (
    <>
      <MenuList component="nav">
        {basicMenu
          .filter((position) => isHasAccess(position?.role, user))
          .map((item, index) => (
            <MenuItem
              component={Link}
              to={item.href}
              key={item.name}
              sx={{ py: 1 }}
              selected={location.pathname === item.href || (0 === index && location.pathname === '/')}
            >
              <ListItemIcon>
                <item.Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{item.name}</ListItemText>
            </MenuItem>
          ))}
      </MenuList>
      <MenuList
        component="nav"
        disablePadding
      >
        {isError && (
          <>
            <Divider sx={{ mt: 0 }} />
            <ListSubheader
              component="div"
              id="nested-list-subheader"
              sx={{ textAlign: 'center' }}
            >
              Error fetching history
            </ListSubheader>
            <Divider sx={{ mb: 1 }} />
          </>
        )}
        {groupByMonth(conversationHistory).map((groupedConversation, index) => [
          <List
            component="div"
            disablePadding
            dense
            subheader={
              <>
                <Divider sx={{ mt: index === 0 ? 0 : 2 }} />
                <ListSubheader
                  component="div"
                  id="nested-list-subheader"
                  sx={{ textAlign: 'center' }}
                >
                  {groupedConversation.name}
                </ListSubheader>
                <Divider sx={{ mb: 1 }} />
              </>
            }
          >
            {groupedConversation.conversations.map((conversation) => (
              <MenuItem
                component={Link}
                to={`/conversation/${conversation.chat_id}`}
                key={conversation.chat_id}
                selected={conversation.chat_id === id}
              >
                <ListItemText>
                  <Typography
                    variant="body2"
                    noWrap
                  >
                    {isSystemMessage(conversation.messages?.[0])
                      ? conversation.messages[0].answer
                      : conversation.first_user_query || conversation?.chat_id}
                  </Typography>
                </ListItemText>
              </MenuItem>
            ))}
          </List>,
        ])}
        {hasNextPage && (
          <Box sx={{ display: 'grid' }}>
            <Button
              sx={{
                textTransform: 'none',
                color: 'primary.dark',
              }}
              onClick={() => fetchNextPage()}
            >
              {t('index.documents.show_more')}
            </Button>
          </Box>
        )}
      </MenuList>
    </>
  )
}
